import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  KeyRound,
  Plus,
  ShieldCheck,
  CreditCard,
  Activity,
  Copy,
  Check,
  AlertTriangle,
  Clock,
  Terminal,
  Code2,
  Trash2,
  ExternalLink,
  RefreshCw,
  Zap,
  Info,
  DollarSign,
  ArrowRight,
  Lock,
  Gem
} from "lucide-react";
import {
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  fetchCreditBalance,
  topupCredits,
  fetchUsageSummary,
  fetchUsageEvents,
  fetchSubscriptionUsage,
} from "../../api/apiKeysApi";
import type {
  ApiKeyItem,
  ApiKeyCreatedResponse,
  CreditBalanceData,
  UsageSummaryData,
  UsageEventLogItem,
  SubscriptionUsageData,
} from "../../api/apiKeysApi";
import "../../styles/api-access.css";

function extractErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (!data) return typeof err?.message === "string" ? err.message : fallback;
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.detail?.error?.message === "string") return data.detail.error.message;
  if (typeof data.detail?.error === "string") return data.detail.error;
  if (typeof data.detail?.message === "string") return data.detail.message;
  if (typeof data.error?.message === "string") return data.error.message;
  if (typeof data.error === "string") return data.error;
  if (typeof data.message === "string") return data.message;
  return fallback;
}

export default function ApiAccessPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"keys" | "usage" | "docs">("keys");
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [balance, setBalance] = useState<CreditBalanceData | null>(null);
  const [usage, setUsage] = useState<UsageSummaryData | null>(null);
  const [events, setEvents] = useState<UsageEventLogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Subscription Entitlement & Quota state
  const [subUsage, setSubUsage] = useState<SubscriptionUsageData | null>(null);
  const [subLoading, setSubLoading] = useState<boolean>(true);
  const [subError, setSubError] = useState<string | null>(null);

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newKeyName, setNewKeyName] = useState<string>("");
  const [newKeyRateLimit, setNewKeyRateLimit] = useState<number>(60);
  const [newKeyExpiryDays, setNewKeyExpiryDays] = useState<number | null>(null);
  const [isSubmittingKey, setIsSubmittingKey] = useState<boolean>(false);

  // One-time secret display modal
  const [createdKeyData, setCreatedKeyData] = useState<ApiKeyCreatedResponse | null>(null);
  const [copiedSecret, setCopiedSecret] = useState<boolean>(false);

  // Revoke modal state
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKeyItem | null>(null);
  const [isRevoking, setIsRevoking] = useState<boolean>(false);

  // Top-up modal state
  const [showTopupModal, setShowTopupModal] = useState<boolean>(false);
  const [topupAmount, setTopupAmount] = useState<number>(1000);
  const [isSubmittingTopup, setIsSubmittingTopup] = useState<boolean>(false);

  // Load all portal data
  const loadPortalData = useCallback(async (isSilent: boolean = false) => {
    if (!isSilent) {
      setLoading(true);
      setSubLoading(true);
    } else {
      setRefreshing(true);
    }
    setSubError(null);

    try {
      // 1. First verify server-side subscription entitlement
      const subRes = await fetchSubscriptionUsage();
      setSubUsage(subRes);
      setSubLoading(false);

      if (!subRes.is_eligible) {
        // Free-tier / Ineligible user -> render restriction card immediately, don't trigger 403s
        setLoading(false);
        setRefreshing(false);
        return;
      }

      // 2. Active Business Pro or Enterprise: Load keys, balance, and usage
      const [keysRes, balRes, usageRes, eventsRes] = await Promise.all([
        fetchApiKeys(),
        fetchCreditBalance(),
        fetchUsageSummary(30),
        fetchUsageEvents(50)
      ]);

      setKeys(keysRes);
      setBalance(balRes);
      setUsage(usageRes);
      setEvents(eventsRes);
    } catch (err: any) {
      console.error("Failed to load API portal data", err);
      if (err?.response?.status === 403 && err?.response?.data?.detail?.error?.code === "SUBSCRIPTION_REQUIRED") {
        setSubUsage({
          client_id: "",
          plan: "free",
          role: "client",
          is_eligible: false,
          entitlement_status: "inactive",
          reason: "Upgrade to Business Pro to unlock Threat Intelligence API access.",
          quota: 0,
          used: 0,
          remaining: 0,
          usage_percent: 0,
          period_start: "",
          period_end: "",
          period_key: "",
          reset_timestamp: "",
          reset_seconds: 0,
          days_remaining: 0,
          warning_level: "normal"
        });
        setSubLoading(false);
      } else {
        setSubError(extractErrorMessage(err, "Failed to load API portal data"));
      }
    } finally {
      setLoading(false);
      setSubLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  // Handle Create Key
  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error("Please enter a descriptive name for the API key");
      return;
    }

    setIsSubmittingKey(true);
    try {
      const res = await createApiKey({
        name: newKeyName.trim(),
        scopes: ["intelligence:read", "sectors:read"],
        rate_limit_per_minute: newKeyRateLimit,
        expires_in_days: newKeyExpiryDays
      });

      setShowCreateModal(false);
      setNewKeyName("");
      setCreatedKeyData(res.data);
      toast.success("API key successfully generated!");
      loadPortalData(true);
    } catch (err: any) {
      toast.error(extractErrorMessage(err, "Failed to generate API key"));
    } finally {
      setIsSubmittingKey(false);
    }
  };

  // Handle Revoke Key
  const handleConfirmRevoke = async () => {
    if (!keyToRevoke) return;
    setIsRevoking(true);
    try {
      await revokeApiKey(keyToRevoke.key_id);
      toast.success(`Key '${keyToRevoke.name}' permanently revoked.`);
      setKeyToRevoke(null);
      loadPortalData(true);
    } catch (err: any) {
      toast.error(extractErrorMessage(err, "Failed to revoke API key"));
    } finally {
      setIsRevoking(false);
    }
  };

  // Handle Top-up
  const handleConfirmTopup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (topupAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsSubmittingTopup(true);
    try {
      const res = await topupCredits(topupAmount);
      toast.success(res?.message || `Successfully added ₹${topupAmount} to credit balance!`);
      setShowTopupModal(false);
      loadPortalData(true);
    } catch (err: any) {
      toast.error(extractErrorMessage(err, "Top-up failed"));
    } finally {
      setIsSubmittingTopup(false);
    }
  };

  const copyToClipboard = async (text: string, isSecret = false) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      if (isSecret) {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 3000);
      }
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const balancePaise = balance?.balance_paise ?? 0;
  const balanceInr = balance?.balance_inr ?? 0;
  const pricePaise = balance?.price_per_request_paise ?? 800;
  const availableRequests = Math.floor(balancePaise / pricePaise);

  // 1. Loading state (smooth skeleton/spinner, avoids layout shift)
  if (subLoading) {
    return (
      <div className="tx-api-portal" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#94a3b8" }}>
          <RefreshCw size={32} className="spin" style={{ margin: "0 auto 16px", color: "#6366f1" }} />
          <p style={{ fontSize: "14px", fontWeight: 500 }}>Verifying API subscription entitlement...</p>
        </div>
      </div>
    );
  }

  // 2. Error state with retry
  if (subError) {
    return (
      <div className="tx-api-portal" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: "440px", background: "rgba(15,23,42,0.85)", padding: "32px", borderRadius: "16px", border: "1px solid rgba(239,68,68,0.25)", boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}>
          <AlertTriangle size={36} style={{ color: "#ef4444", margin: "0 auto 16px" }} />
          <h3 style={{ fontSize: "18px", color: "#fff", fontWeight: 700, marginBottom: "8px" }}>Subscription Verification Error</h3>
          <p style={{ fontSize: "13px", color: "#94a3b8", lineHeight: 1.5, marginBottom: "20px" }}>{subError}</p>
          <button
            type="button"
            className="tx-btn tx-btn-primary"
            onClick={() => loadPortalData()}
          >
            <RefreshCw size={14} /> Retry Verification
          </button>
        </div>
      </div>
    );
  }

  // 3. Restriction View for Free / Ineligible Tier
  if (subUsage && !subUsage.is_eligible) {
    return (
      <div className="w-full px-6 py-6 sm:px-8">
        {/* Large Page Title exact same as Image 2 */}
        <h1 className="tx-exclusive-page-title">
          API access
        </h1>

        {/* Gold Restriction Banner matching Image 2 */}
        <div className="tx-api-restriction-banner" style={{ maxWidth: "1000px" }}>
          <div className="tx-api-restriction-banner-text">
            <span>Upgrade to Business Pro to unlock Threat Intelligence API access.</span>
          </div>
          <button
            type="button"
            className="tx-api-restriction-banner-btn"
            style={{ fontWeight: 500 }}
            onClick={() => navigate("/subscription")}
          >
            <div className="flex justify-center items-center">
              <img
                alt=""
                className="w-[14px] h-[14px] my-auto mr-[4px]"
                src="https://d1898qjf7hzy9p.cloudfront.net/icons/premium-diamond.svg"
              />
              <span>Upgrade</span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tx-api-portal">
      {/* Portal Header */}
      <div className="tx-api-header">
        <div className="tx-api-title-row">
          <div className="tx-api-icon-badge">
            <KeyRound size={26} />
          </div>
          <div>
            <h1 className="tx-api-title">API Access & Developer Portal</h1>
            <p className="tx-api-subtitle">
              Automate threat intelligence ingestion with versioned REST APIs. Included in Business Pro (1,000 req/mo).
            </p>
          </div>
        </div>

        <div className="tx-api-header-actions">
          <button
            type="button"
            className="tx-btn tx-btn-secondary tx-btn-icon"
            onClick={async () => {
              await loadPortalData(true);
              toast.success("Telemetry & keys refreshed");
            }}
            disabled={refreshing}
            title="Refresh stats and keys"
            aria-label="Refresh stats and keys"
            style={{
              width: "38px",
              height: "38px",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px"
            }}
          >
            <RefreshCw size={16} className={refreshing ? "tx-spin" : ""} />
          </button>
          <button
            type="button"
            className="tx-btn tx-btn-secondary"
            onClick={() => setShowTopupModal(true)}
          >
            <CreditCard size={15} />
            <span>Add Credits</span>
          </button>
          <button
            type="button"
            className="tx-btn tx-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={16} />
            <span>Generate API Key</span>
          </button>
        </div>
      </div>

      {/* Monthly Quota Indicator Banner for Business Pro / Enterprise */}
      {subUsage && subUsage.plan === "Business Pro" && (
        <div className="tx-api-quota-banner">
          <div className="tx-api-quota-top">
            <div className="tx-api-quota-title-area">
              <span className="tx-api-quota-badge">Business Pro Monthly Quota</span>
              <span className="tx-api-quota-stat-text">
                {subUsage.used.toLocaleString()} / {subUsage.quota.toLocaleString()} requests used
              </span>
              <span className="tx-api-quota-remaining-text">
                ({subUsage.remaining.toLocaleString()} remaining)
              </span>
            </div>
            <div>
              {subUsage.warning_level === "warning_80" && (
                <span className="tx-api-warning-pill warning_80">
                  <AlertTriangle size={12} /> Approaching Limit (80% used)
                </span>
              )}
              {subUsage.warning_level === "critical_95" && (
                <span className="tx-api-warning-pill critical_95">
                  <AlertTriangle size={12} /> Urgent: 95% Quota Consumed
                </span>
              )}
              {subUsage.warning_level === "exhausted_100" && (
                <span className="tx-api-warning-pill exhausted_100">
                  <AlertTriangle size={12} /> Monthly Allowance Exhausted (1,000 / 1,000 used)
                </span>
              )}
            </div>
          </div>

          <div className="tx-api-quota-bar-track">
            <div
              className={`tx-api-quota-bar-fill ${subUsage.warning_level}`}
              style={{ width: `${Math.min(100, subUsage.usage_percent)}%` }}
            />
          </div>

          <div className="tx-api-quota-footer">
            <span>
              Includes 1,000 monthly billable requests at ₹0 charge.
            </span>
            <span>
              {subUsage.reset_timestamp ? `Resets on ${new Date(subUsage.reset_timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} (${subUsage.days_remaining} days remaining)` : "Monthly cycle"}
            </span>
          </div>
        </div>
      )}

      {subUsage && subUsage.plan === "Enterprise" && (
        <div className="tx-api-quota-banner">
          <div className="tx-api-quota-top">
            <div className="tx-api-quota-title-area">
              <span className="tx-api-quota-badge">Enterprise Tier</span>
              <span className="tx-api-quota-stat-text">
                Enterprise Custom Quota & Dedicated Rate Limits
              </span>
            </div>
            <span className="tx-api-quota-remaining-text">
              Active Agreement
            </span>
          </div>
        </div>
      )}

      {/* Overview Stat Grid */}
      <div className="tx-stat-grid">
        {/* Prepaid Balance */}
        <div className="tx-stat-card">
          <div className="tx-stat-icon-wrapper" style={{ color: "#38bdf8" }}>
            <DollarSign size={22} />
          </div>
          <div className="tx-stat-content">
            <div className="tx-stat-label">Prepaid Balance</div>
            <div className="tx-stat-value">₹{balanceInr.toFixed(2)}</div>
            <div className="tx-stat-subtext">
              ~{availableRequests.toLocaleString()} billable requests (₹8.00 / query)
            </div>
          </div>
        </div>

        {/* Total Usage */}
        <div className="tx-stat-card">
          <div className="tx-stat-icon-wrapper" style={{ color: "#a855f7" }}>
            <Activity size={22} />
          </div>
          <div className="tx-stat-content">
            <div className="tx-stat-label">30-Day Requests</div>
            <div className="tx-stat-value">
              {usage?.total_requests?.toLocaleString() ?? 0}
            </div>
            <div className="tx-stat-subtext">
              {usage?.billable_requests?.toLocaleString() ?? 0} billable · {usage?.non_billable_requests ?? 0} operational
            </div>
          </div>
        </div>

        {/* Total Incurred Charges */}
        <div className="tx-stat-card">
          <div className="tx-stat-icon-wrapper" style={{ color: "#22c55e" }}>
            <Zap size={22} />
          </div>
          <div className="tx-stat-content">
            <div className="tx-stat-label">Total Incurred</div>
            <div className="tx-stat-value">
              ₹{(usage?.total_amount_inr ?? 0).toFixed(2)}
            </div>
            <div className="tx-stat-subtext">
              Durable idempotent ledger accounting
            </div>
          </div>
        </div>

        {/* Server Monotonic Latency */}
        <div className="tx-stat-card">
          <div className="tx-stat-icon-wrapper" style={{ color: "#f59e0b" }}>
            <Clock size={22} />
          </div>
          <div className="tx-stat-content">
            <div className="tx-stat-label">Latency (Avg / P95)</div>
            <div className="tx-stat-value">
              {usage?.avg_latency_ms ?? 0}ms <span style={{ fontSize: 14, color: "#94a3b8" }}>/ {usage?.p95_latency_ms ?? 0}ms</span>
            </div>
            <div className="tx-stat-subtext">
              Monotonic server execution duration
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tx-tabs-nav">
        <button
          type="button"
          className={`tx-tab-btn ${activeTab === "keys" ? "active" : ""}`}
          onClick={() => setActiveTab("keys")}
        >
          <KeyRound size={16} />
          <span>API Keys ({keys.length})</span>
        </button>
        <button
          type="button"
          className={`tx-tab-btn ${activeTab === "usage" ? "active" : ""}`}
          onClick={() => setActiveTab("usage")}
        >
          <Activity size={16} />
          <span>Usage Metering & Audit Ledger</span>
        </button>
        <button
          type="button"
          className={`tx-tab-btn ${activeTab === "docs" ? "active" : ""}`}
          onClick={() => setActiveTab("docs")}
        >
          <Code2 size={16} />
          <span>Integration Guide & SDKs</span>
        </button>
      </div>

      {/* TAB 1: API KEYS */}
      {activeTab === "keys" && (
        <div className="tx-tab-content">
          <div className="tx-table-container">
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Key Name & Prefix</th>
                  <th>Status</th>
                  <th>Rate Limit</th>
                  <th>Permission Scopes</th>
                  <th>Created</th>
                  <th>Last Used</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
                      No active API keys found. Click "Generate API Key" above to create your first client secret.
                    </td>
                  </tr>
                ) : (
                  keys.map((k) => (
                    <tr key={k.key_id}>
                      <td>
                        <div style={{ fontWeight: 600, color: "#f8fafc", marginBottom: 3 }}>
                          {k.name}
                        </div>
                        <div style={{ fontFamily: "monospace", fontSize: 12, color: "#38bdf8" }}>
                          {k.prefix}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`tx-badge ${
                            k.status === "active"
                              ? "tx-badge-active"
                              : k.status === "revoked"
                              ? "tx-badge-revoked"
                              : "tx-badge-free"
                          }`}
                        >
                          {k.status}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: "#cbd5e1" }}>{k.rate_limit_per_minute} req/min</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {k.scopes.map((s) => (
                            <span key={s} className="tx-badge tx-badge-free" style={{ fontSize: 10 }}>
                              {s}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: "#94a3b8" }}>
                        {k.created_at ? new Date(k.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td style={{ fontSize: 12, color: "#94a3b8" }}>
                        {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "Never"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {k.status === "active" ? (
                          <button
                            type="button"
                            className="tx-btn tx-btn-danger"
                            style={{ padding: "5px 10px", fontSize: 12 }}
                            onClick={() => setKeyToRevoke(k)}
                          >
                            <Trash2 size={13} />
                            <span>Revoke</span>
                          </button>
                        ) : (
                          <span style={{ fontSize: 12, color: "#64748b" }}>Revoked</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: USAGE METERING & AUDIT LEDGER */}
      {activeTab === "usage" && (
        <div className="tx-tab-content">
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: "#f8fafc", margin: "0 0 6px" }}>
              Idempotent Request Ledger
            </h3>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>
              Each data query is billed at exactly 800 paise (₹8.00). Retries with the same <code>Idempotency-Key</code> are replayed free of charge.
            </p>
          </div>

          <div className="tx-table-container">
            <table className="tx-table">
              <thead>
                <tr>
                  <th style={{ width: "110px" }}>Timestamp</th>
                  <th>Method & Endpoint</th>
                  <th style={{ width: "80px" }}>Status</th>
                  <th style={{ width: "95px" }}>Latency</th>
                  <th style={{ width: "110px" }}>Type</th>
                  <th style={{ width: "90px" }}>Price</th>
                  <th style={{ width: "150px" }}>Idempotency Reference</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
                      No usage events recorded yet. Start making API requests with your API key to view live audit traces.
                    </td>
                  </tr>
                ) : (
                  events.map((ev) => (
                    <tr key={ev.event_id}>
                      <td style={{ fontSize: 12, color: "#94a3b8" }}>
                        {ev.timestamp ? new Date(ev.timestamp).toLocaleTimeString() : "—"}
                      </td>
                      <td style={{ maxWidth: "340px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                          <span
                            className="tx-badge tx-badge-free"
                            style={{ fontWeight: 700, fontSize: 10, flexShrink: 0 }}
                          >
                            {ev.http_method}
                          </span>
                          <span
                            style={{ fontFamily: "monospace", fontSize: 12, color: "#e2e8f0", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
                            title={ev.endpoint}
                          >
                            {ev.endpoint}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`tx-badge ${
                            ev.status_code >= 200 && ev.status_code < 300
                              ? "tx-badge-active"
                              : "tx-badge-revoked"
                          }`}
                        >
                          {ev.status_code}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: "#cbd5e1" }}>
                        {ev.duration_ms} ms
                      </td>
                      <td>
                        <span
                          className={`tx-badge ${
                            ev.billable ? "tx-badge-billable" : "tx-badge-free"
                          }`}
                        >
                          {ev.billable ? "Billable" : "Non-billable"}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: ev.billable ? "#fbbf24" : "#94a3b8" }}>
                        {ev.billable ? `₹${(ev.price_paise / 100).toFixed(2)}` : "₹0.00"}
                      </td>
                      <td style={{ fontSize: 11, fontFamily: "monospace", color: "#64748b", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={ev.idempotency_key || ev.request_id}>
                        {ev.idempotency_key || ev.request_id || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: INTEGRATION GUIDE & SDKs */}
      {activeTab === "docs" && (
        <div className="tx-tab-content" style={{ display: "grid", gap: 24 }}>
          {/* Section 1: Authentication */}
          <div style={{ background: "rgba(15, 23, 42, 0.65)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
              <Terminal size={18} color="#38bdf8" />
              <span>Authentication & cURL Example</span>
            </h3>
            <p style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.5, margin: "0 0 16px" }}>
              Authenticate requests by including your secret API key in the <code>Authorization: Bearer</code> header.
              Always store your API key in an environment variable (e.g., <code>THREATACTIX_API_KEY</code>) on your backend server. Never expose it in client-side code.
            </p>

            <div className="tx-code-block">
              <button
                type="button"
                className="tx-copy-btn"
                onClick={() =>
                  copyToClipboard(`curl -X GET "http://localhost:8000/api/v1/intelligence?page=1&page_size=10" \\
  -H "Authorization: Bearer $THREATACTIX_API_KEY" \\
  -H "Idempotency-Key: $(uuidgen)"`)
                }
              >
                <Copy size={12} />
                <span>Copy</span>
              </button>
              <pre style={{ margin: 0 }}>
{`curl -X GET "http://localhost:8000/api/v1/intelligence?page=1&page_size=10" \\
  -H "Authorization: Bearer $THREATACTIX_API_KEY" \\
  -H "Idempotency-Key: $(uuidgen)"`}
              </pre>
            </div>
          </div>

          {/* Section 2: Python Integration */}
          <div style={{ background: "rgba(15, 23, 42, 0.65)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
              <Code2 size={18} color="#a855f7" />
              <span>Python 3 Example (requests)</span>
            </h3>
            <div className="tx-code-block">
              <button
                type="button"
                className="tx-copy-btn"
                onClick={() =>
                  copyToClipboard(`import os
import uuid
import requests

API_KEY = os.environ.get("THREATACTIX_API_KEY")
BASE_URL = "http://localhost:8000/api/v1"

headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Idempotency-Key": str(uuid.uuid4())
}

# 1. Query live intelligence feed (Billable: ₹8)
response = requests.get(
    f"{BASE_URL}/intelligence",
    headers=headers,
    params={"sector": "financial_services", "page_size": 10}
)

if response.status_code == 200:
    res = response.json()
    print(f"Charge: ₹{res['meta']['charge_inr']} | Remaining Balance: {res['meta']['remaining_credits_paise']} paise")
    for incident in res["data"]:
        print(f"[{incident['threat_level'].upper()}] {incident['title']} ({incident['primary_sector']})")
elif response.status_code == 402:
    print("Insufficient prepaid credits! Please top up at /api/v1/billing/topup")
`)
                }
              >
                <Copy size={12} />
                <span>Copy</span>
              </button>
              <pre style={{ margin: 0 }}>
{`import os
import uuid
import requests

API_KEY = os.environ.get("THREATACTIX_API_KEY")
BASE_URL = "http://localhost:8000/api/v1"

headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Idempotency-Key": str(uuid.uuid4())
}

# 1. Query live intelligence feed (Billable: ₹8)
response = requests.get(
    f"{BASE_URL}/intelligence",
    headers=headers,
    params={"sector": "financial_services", "page_size": 10}
)

if response.status_code == 200:
    res = response.json()
    print(f"Charge: ₹{res['meta']['charge_inr']} | Remaining: {res['meta']['remaining_credits_paise']} paise")
    for incident in res["data"]:
        print(f"[{incident['threat_level'].upper()}] {incident['title']} ({incident['primary_sector']})")
elif response.status_code == 402:
    print("Insufficient prepaid credits! Please top up.")`}
              </pre>
            </div>
          </div>

          {/* Section 3: Billing & Error Semantics */}
          <div style={{ background: "rgba(15, 23, 42, 0.65)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc", margin: "0 0 10px" }}>
              Pricing & Error Code Specifications
            </h3>
            <ul style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>
                <strong>₹8.00 (800 paise) per billable query:</strong> Charged ONLY when data retrieval succeeds (HTTP 200).
              </li>
              <li>
                <strong>Free Operational Calls:</strong> <code>/health</code>, <code>/usage</code>, <code>/keys</code>, and billing endpoints are always 0 paise.
              </li>
              <li>
                <strong>Idempotency Header:</strong> Supply <code>Idempotency-Key: &lt;UUID&gt;</code> to safely retry queries without risk of duplicate charges.
              </li>
              <li>
                <strong>HTTP 402 Payment Required:</strong> Returned if prepaid credit balance is below 800 paise. Zero overdraft allowed.
              </li>
              <li>
                <strong>HTTP 429 Too Many Requests:</strong> Default limit is 60 requests/minute. Includes standard <code>X-RateLimit-*</code> headers.
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* MODAL 1: Create API Key */}
      {showCreateModal && (
        <div className="tx-modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="tx-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="tx-modal-title">
              <KeyRound size={20} color="#38bdf8" />
              <span>Generate API Key</span>
            </h2>
            <p className="tx-modal-subtitle">
              API keys allow external server scripts and SIEMs to query Threatactix intelligence.
            </p>

            <form onSubmit={handleCreateKey}>
              <div className="tx-form-group">
                <label className="tx-form-label">Key Name / Description</label>
                <input
                  type="text"
                  className="tx-form-input"
                  placeholder="e.g. Production SIEM Ingestion, Splunk Connector"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="tx-form-group">
                <label className="tx-form-label">Rate Limit (Requests / Minute)</label>
                <input
                  type="number"
                  className="tx-form-input"
                  min={10}
                  max={300}
                  value={newKeyRateLimit}
                  onChange={(e) => setNewKeyRateLimit(Number(e.target.value))}
                />
              </div>

              <div className="tx-form-group">
                <label className="tx-form-label">Expiration Period</label>
                <select
                  className="tx-form-input"
                  value={newKeyExpiryDays || ""}
                  onChange={(e) => setNewKeyExpiryDays(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Never Expires</option>
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                  <option value="365">1 Year</option>
                </select>
              </div>

              <div className="tx-modal-actions">
                <button
                  type="button"
                  className="tx-btn tx-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tx-btn tx-btn-primary"
                  disabled={isSubmittingKey}
                >
                  {isSubmittingKey ? "Generating..." : "Generate Key"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: One-Time Secret Display */}
      {createdKeyData && (
        <div className="tx-modal-backdrop">
          <div className="tx-modal-content" style={{ borderColor: "#f59e0b" }}>
            <h2 className="tx-modal-title" style={{ color: "#fbbf24" }}>
              <ShieldCheck size={22} color="#fbbf24" />
              <span>Save Your API Secret Key</span>
            </h2>
            <p className="tx-modal-subtitle">
              Your key <strong>{createdKeyData.name}</strong> has been generated.
            </p>

            <div className="tx-secret-box">
              <div className="tx-secret-warning">
                <AlertTriangle size={16} />
                <span>CRITICAL: This secret will NEVER be shown again!</span>
              </div>
              <p style={{ fontSize: 12, color: "#cbd5e1", margin: "0 0 10px" }}>
                Threatactix only stores an irreversible HMAC-SHA256 digest on the server. If you lose this key, you must generate a new one.
              </p>

              <div className="tx-secret-key-display">
                <span>{createdKeyData.secret_key}</span>
                <button
                  type="button"
                  className="tx-btn tx-btn-primary"
                  style={{
                    padding: "8px 14px",
                    fontSize: 12,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    minWidth: "84px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px"
                  }}
                  onClick={() => copyToClipboard(createdKeyData.secret_key, true)}
                >
                  {copiedSecret ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copiedSecret ? "Copied!" : "Copy"}</span>
                </button>
              </div>
            </div>

            <div className="tx-modal-actions">
              <button
                type="button"
                className="tx-btn tx-btn-primary"
                onClick={() => setCreatedKeyData(null)}
              >
                I Have Saved My Secret Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Revoke Confirmation */}
      {keyToRevoke && (
        <div className="tx-modal-backdrop" onClick={() => setKeyToRevoke(null)}>
          <div className="tx-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="tx-modal-title" style={{ color: "#ef4444" }}>
              <AlertTriangle size={20} color="#ef4444" />
              <span>Revoke API Key</span>
            </h2>
            <p className="tx-modal-subtitle">
              Are you sure you want to revoke key <strong>{keyToRevoke.name}</strong> ({keyToRevoke.prefix})?
              Subsequent API requests using this key will immediately fail with HTTP 401 Unauthorized. This action cannot be undone.
            </p>

            <div className="tx-modal-actions">
              <button
                type="button"
                className="tx-btn tx-btn-secondary"
                onClick={() => setKeyToRevoke(null)}
                disabled={isRevoking}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tx-btn tx-btn-danger"
                onClick={handleConfirmRevoke}
                disabled={isRevoking}
              >
                {isRevoking ? "Revoking..." : "Revoke Key Immediately"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: Top-up Credits */}
      {showTopupModal && (
        <div className="tx-modal-backdrop" onClick={() => setShowTopupModal(false)}>
          <div className="tx-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="tx-modal-title">
              <CreditCard size={20} color="#38bdf8" />
              <span>Recharge Prepaid API Credits (Test Adapter)</span>
            </h2>
            <p className="tx-modal-subtitle">
              Prepaid balance is deducted at exactly ₹8.00 (800 paise) per billable query. <em>[Test-Mode Adapter: Instant simulated credit grant without live payment gateway]</em>
            </p>

            <form onSubmit={handleConfirmTopup}>
              <div className="tx-form-group">
                <label className="tx-form-label">Select Amount (INR)</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 12 }}>
                  {[500, 1000, 2500].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className={`tx-btn ${topupAmount === amt ? "tx-btn-primary" : "tx-btn-secondary"}`}
                      style={{ justifyContent: "center" }}
                      onClick={() => setTopupAmount(amt)}
                    >
                      ₹{amt}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  className="tx-form-input"
                  min={100}
                  step={100}
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(Number(e.target.value))}
                />
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                  Provides ~{Math.floor((topupAmount * 100) / 800)} successful intelligence requests.
                </div>
              </div>

              <div className="tx-modal-actions">
                <button
                  type="button"
                  className="tx-btn tx-btn-secondary"
                  onClick={() => setShowTopupModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="tx-btn tx-btn-primary"
                  disabled={isSubmittingTopup}
                >
                  {isSubmittingTopup ? "Processing..." : `Recharge ₹${topupAmount}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
